import React, { createContext, useState, useContext, useCallback } from 'react';
import config from '../config';
import { getUserIdFromUrl } from '../helper/apiHelper';
import { useMaintenanceMode } from './MaintenanceContext';
import { useStatic } from './StaticContext';

// Context oluştur
const BonusContext = createContext();

// Bonus verisini sağlayacak context provider
export const BonusProvider = ({ children }) => {
  const [bonusData, setBonusData] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [gamesData, setGamesData] = useState([]);
  const [gamesTabs, setGamesTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gamesError, setGamesError] = useState(null);
  const userId = getUserIdFromUrl();
  const [userName, setUserName] = useState('');
  const { isMaintenanceMode } = useMaintenanceMode(); // Bakım modu bilgisini al
  const { isLanguage, selectedLanguage } = useStatic(); // Dil ayarlarını al

  // Hata tipini belirleyen yardımcı fonksiyon
  const determineErrorType = (error, response) => {
    // CORS hatası kontrolü
    if (error && (
      error.message?.includes('CORS') || 
      error.message?.includes('blocked by CORS policy') ||
      error.message?.includes('Cross-Origin Request Blocked') ||
      error.name === 'TypeError' && error.message?.includes('Failed to fetch')
    )) {
      return "user_blocked";
    }

    // Response varsa status koduna göre kontrol et
    if (response) {
      if (response.status === 403) {
        return "user_blocked";
      }
      if (response.status === 404) {
        return "api_error";
      }
      if (response.status >= 500) {
        return "api_error";
      }
    }

    // Network hatası
    if (error && (
      error.message?.includes('NetworkError') ||
      error.message?.includes('Failed to fetch') ||
      error.name === 'TypeError'
    )) {
      return "connection_error";
    }

    return "connection_error";
  };

  const fetchBonusForUser = useCallback(async () => {
    // Önce userId kontrolü yap
    if (!userId) {
      setError("invalid_access");
      setLoading(false);
      return;
    }

    try {
      // İstek payload'ını oluştur
      const payload = {
        process_id: "getc-bonuses",
        user_id: userId,
        recaptcha: "test",
      };

      // Bakım modu kontrolü
      if (isMaintenanceMode) {
        payload.dev = "10";
      }

      // Dil desteği kontrolü
      if (isLanguage && selectedLanguage) {
        payload.lang = selectedLanguage;
      }

      let body = JSON.stringify(payload);
      let keys = await window.rh.cr(body);
      
      const response = await fetch(`${config.apiBaseUrl}`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          "hk": keys[0],
          "hh": keys[1]
        },
        body: body,
      });

      // 403 veya CORS hatası kontrolü
      if (response.status === 403) {
        console.error('❌ 403 Kullanıcı engellendi:', response.status);
        setError("user_blocked");
        setLoading(false);
        return;
      }

      const result = await response.json();

      if (result.status) {
        setUserName(result.full_name);
        setBonusData(result.data.talep_data);
        setTabs(result.data.bonus_type_tabs);
        setError(null);
        setLoading(false);
      } else if (result.message === "Bilinmeyen kullanıcı") {
        setError("user_not_found");
        setLoading(false);
      } else {
        setError("api_error");
        setLoading(false);
      }
    } catch (error) {
      console.error("❌ Bonus API isteği sırasında hata oluştu:", error);
      
      // Hata tipini belirle
      const errorType = determineErrorType(error, null);
      console.error('🔍 Belirlenen hata tipi:', errorType);
      
      setError(errorType);
      setLoading(false);
    }
  }, [userId, isMaintenanceMode, isLanguage, selectedLanguage]);

  const fetchGamesForUser = useCallback(async () => {
    // Önce userId kontrolü yap
    if (!userId) {
      setGamesError("invalid_access");
      setGamesLoading(false);
      return;
    }

    try {
      // İstek payload'ını oluştur
      const payload = {
        process_id: "getc-games",
        user_id: userId,
        recaptcha: "test",
      };

      // Bakım modu kontrolü
      if (isMaintenanceMode) {
        payload.dev = "10";
      }

      // Dil desteği kontrolü
      if (isLanguage && selectedLanguage) {
        payload.lang = selectedLanguage;
      }

      let body = JSON.stringify(payload);
      let keys = await window.rh.cr(body);
      
      const response = await fetch(`${config.apiBaseUrl}`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          "hk": keys[0],
          "hh": keys[1]
        },
        body: body,
      });

      // 403 veya CORS hatası kontrolü
      if (response.status === 403) {
        console.error('❌ 403 Kullanıcı engellendi:', response.status);
        setGamesError("user_blocked");
        setGamesLoading(false);
        return;
      }

      const result = await response.json();

      if (result.status) {
        setGamesData(result.data.talep_data);
        setGamesTabs(result.data.bonus_type_tabs || []);
        setGamesError(null);
        setGamesLoading(false);
      } else if (result.message === "Bilinmeyen kullanıcı") {
        setGamesError("user_not_found");
        setGamesLoading(false);
      } else {
        setGamesError("api_error");
        setGamesLoading(false);
      }
    } catch (error) {
      console.error("❌ Oyunlar API isteği sırasında hata oluştu:", error);
      
      // Hata tipini belirle
      const errorType = determineErrorType(error, null);
      console.error('🔍 Belirlenen hata tipi:', errorType);
      
      setGamesError(errorType);
      setGamesLoading(false);
    }
  }, [userId, isMaintenanceMode, isLanguage, selectedLanguage]);

  // Bonus verisi, loading durumu ve hata bilgisini sağlayan context
  return (
    <BonusContext.Provider value={{ 
      bonusData, 
      loading, 
      error, 
      tabs, 
      fetchBonusForUser, 
      gamesData,
      gamesLoading,
      gamesError,
      gamesTabs,
      fetchGamesForUser,
      userName, 
      userId 
    }}>
      {children}
    </BonusContext.Provider>
  );
};

// Context'teki veriye erişmek için kullanılacak özel hook
export const useBonus = () => useContext(BonusContext);